import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { Badge } from '../../components/shared';
import { formatVolume } from '../../utils/format';
import { NEW_TAB_LINK_PROPS } from '../../../../utils/newTab';
import { eventKey } from '../model/fromAssetDetail';
import type { CandidateEvent } from '../model/types';
import { FlowMark } from './FlowMark';
import { leadingOutcome } from './leadingOutcome';

export const CANDIDATE_GRID = 'minmax(0, 1fr) 210px 150px 80px 96px 52px 28px';

interface CandidateEventRowProps {
    event: CandidateEvent;
    color: string | null;
    selected: boolean;
    selectable: boolean;
    onToggle: () => void;
}

export function formatScore(score: number): string {
    return score.toFixed(2);
}

export function formatEndDate(date: Date | null): string {
    if (!date) return '–';
    return date.toLocaleDateString(undefined, {
        year: '2-digit',
        month: 'short',
        day: 'numeric',
    });
}

export function eventHref(event: CandidateEvent): string {
    return `/markets/event/${encodeURIComponent(eventKey(event))}`;
}

export function CandidateEventRow({
    event,
    color,
    selected,
    selectable,
    onToggle,
}: CandidateEventRowProps) {
    const theme = useTheme();
    const lead = leadingOutcome(event);
    const probability = lead?.market.probability ?? null;
    const clickable = selected || selectable;
    const barColor = color ?? theme.palette.text.disabled;

    return (
        <Box
            role="button"
            aria-pressed={selected}
            data-testid={`candidate-${eventKey(event)}`}
            onClick={clickable ? onToggle : undefined}
            title={
                clickable
                    ? undefined
                    : 'At most 4 events can be on the chart at the same time.'
            }
            sx={{
                display: 'grid',
                gridTemplateColumns: CANDIDATE_GRID,
                alignItems: 'center',
                columnGap: 2,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                cursor: clickable ? 'pointer' : 'default',
                opacity: event.open ? 1 : 0.6,
                bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
                '&:hover': {
                    bgcolor: selected
                        ? alpha(theme.palette.primary.main, 0.14)
                        : 'action.hover',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        flexShrink: 0,
                        bgcolor: color ?? 'transparent',
                        border: color ? 0 : 1,
                        borderColor: 'divider',
                    }}
                />
                {event.image && (
                    <Box
                        component="img"
                        src={event.image}
                        alt=""
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 0.5,
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                )}
                <Typography
                    title={event.title}
                    sx={{
                        fontSize: '13px',
                        fontWeight: selected ? 700 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {event.title}
                </Typography>
                {!event.open && (
                    <Badge color={theme.palette.text.disabled}>Closed</Badge>
                )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            flex: 1,
                            height: 5,
                            borderRadius: '3px',
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                        }}
                    >
                        <Box
                            sx={{
                                width: `${(probability ?? 0) * 100}%`,
                                height: '100%',
                                bgcolor: barColor,
                            }}
                        />
                    </Box>
                    <Typography
                        sx={{
                            width: 48,
                            textAlign: 'right',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                        }}
                    >
                        {probability === null
                            ? '–'
                            : `${(probability * 100).toFixed(1)}%`}
                    </Typography>
                </Box>
                {lead && event.markets.length > 1 && (
                    <Typography
                        title={lead.label}
                        sx={{
                            fontSize: '11px',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {lead.label}
                    </Typography>
                )}
            </Box>

            <FlowMark event={event} />

            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                }}
            >
                {formatVolume(event.volume)}
            </Typography>

            <Typography
                sx={{
                    fontSize: '12px',
                    color: 'text.secondary',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                }}
            >
                {formatEndDate(event.endDate)}
            </Typography>

            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    textAlign: 'right',
                }}
            >
                {formatScore(event.score)}
            </Typography>

            <Tooltip title="Open the event page">
                <IconButton
                    size="small"
                    component={RouterLink}
                    to={eventHref(event)}
                    {...NEW_TAB_LINK_PROPS}
                    aria-label={`Open the event page: ${event.title}`}
                    onClick={e => e.stopPropagation()}
                    sx={{ p: 0.25 }}
                >
                    <OpenInNewIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}
